import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { ChevronRightIcon } from "../components/ui/Icons";

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { login, isLoggingIn, loginError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/dashboard";

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    login(
      { email, password },
      {
        onSuccess: () => navigate(from, { replace: true }),
      }
    );
  };

  return (
    <div className="relative flex min-h-screen">
      {/* Background effects */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -left-1/4 top-0 h-[600px] w-[600px] rounded-full bg-amber/5 blur-[120px]" />
        <div className="absolute -right-1/4 bottom-0 h-[600px] w-[600px] rounded-full bg-purple-500/5 blur-[120px]" />
        <div className="absolute inset-0 bg-grid opacity-30" />
      </div>

      {/* Left side - Branding */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden border-r border-elevated p-12 lg:flex">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <span className="font-display text-xl font-medium tracking-tight text-secondary">
            plugins for <span className="text-amber">claude code</span>
          </span>
        </Link>

        {/* Hero content */}
        <div className="max-w-md">
          <h1 className="mb-6 font-display text-4xl font-bold leading-tight tracking-tight">
            Extend your
            <br />
            <span className="text-gradient">Claude Code</span>
            <br />
            experience
          </h1>
          <p className="text-lg leading-relaxed text-secondary">
            Discover powerful plugins, share your creations, and join a community of developers
            building the future of AI-assisted coding.
          </p>
        </div>

        {/* Decorative terminal */}
        <div className="overflow-hidden rounded-xl border border-elevated bg-surface/80">
          <div className="flex items-center gap-2 border-b border-elevated px-4 py-3">
            <div className="h-3 w-3 rounded-full bg-danger/60" />
            <div className="h-3 w-3 rounded-full bg-warning/60" />
            <div className="h-3 w-3 rounded-full bg-success/60" />
            <span className="ml-2 font-mono text-xs text-muted">terminal</span>
          </div>
          <div className="p-4 font-mono text-sm">
            <p className="text-muted">
              <span className="text-amber">$</span> claude plugins install syntax-highlighter
            </p>
            <p className="mt-1 text-success">✓ Plugin installed successfully</p>
            <p className="mt-2 text-muted">
              <span className="text-amber">$</span> claude plugins list
            </p>
            <p className="mt-1 text-secondary">
              <span className="text-amber">→</span> syntax-highlighter{" "}
              <span className="text-muted">v2.1.0</span>
            </p>
            <p className="text-secondary">
              <span className="text-amber">→</span> code-formatter{" "}
              <span className="text-muted">v1.4.2</span>
            </p>
            <p className="text-secondary">
              <span className="text-amber">→</span> git-helper{" "}
              <span className="text-muted">v3.0.1</span>
            </p>
            <p className="mt-2 animate-pulse text-muted">
              <span className="text-amber">$</span> <span className="text-secondary">_</span>
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="relative flex flex-1 flex-col justify-center px-6 py-12 lg:px-12">
        {/* Mobile logo */}
        <div className="mb-8 flex lg:hidden">
          <Link to="/" className="flex items-center">
            <span className="font-display text-lg font-medium tracking-tight text-secondary">
              plugins for <span className="text-amber">claude code</span>
            </span>
          </Link>
        </div>

        <div className="mx-auto w-full max-w-md">
          <div className="mb-8">
            <h2 className="font-display text-2xl font-bold tracking-tight text-primary">
              Welcome back
            </h2>
            <p className="mt-2 text-secondary">Sign in to your account to continue</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {loginError && (
              <div className="rounded-lg border border-danger/30 bg-danger/10 p-4 text-sm text-danger">
                {loginError.message || "Invalid email or password"}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="mb-2 block font-mono text-sm text-secondary">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="input-field"
                />
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label htmlFor="password" className="font-mono text-sm text-secondary">
                    Password
                  </label>
                  <Link
                    to="/forgot-password"
                    className="font-mono text-xs text-amber hover:text-amber-hover"
                  >
                    Forgot password?
                  </Link>
                </div>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="input-field"
                />
              </div>
            </div>

            <button type="submit" disabled={isLoggingIn} className="btn-primary w-full">
              {isLoggingIn ? (
                <span className="flex items-center gap-2">
                  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Signing in...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Sign in
                  <ChevronRightIcon size={16} />
                </span>
              )}
            </button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-sm text-muted">
              Don't have an account?{" "}
              <Link to="/signup" className="font-medium text-amber hover:text-amber-hover">
                Create one
              </Link>
            </p>
          </div>

          {/* Divider */}
          <div className="my-8 flex items-center gap-4">
            <div className="h-px flex-1 bg-elevated" />
            <span className="font-mono text-xs text-muted">or continue with</span>
            <div className="h-px flex-1 bg-elevated" />
          </div>

          {/* Social logins (placeholder) */}
          <div className="grid grid-cols-2 gap-3">
            <button className="btn-secondary justify-center py-2.5">
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
              </svg>
              GitHub
            </button>
            <button className="btn-secondary justify-center py-2.5">
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Google
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
